import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { FaDatabase, FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Home from './pages/Home';
import Population from './pages/Population';
import Economy from './pages/Economy';
import Health from './pages/Health';
import Education from './pages/Education';
import Compare from './pages/Compare';
import Bomet from './pages/counties/Bomet';

const COUNTIES = [
  { name: "Baringo", path: null },
  { name: "Bomet", path: "/counties/bomet" },
  { name: "Bungoma", path: null },
  { name: "Busia", path: null },
  { name: "Elgeyo Marakwet", path: null },
  { name: "Embu", path: null },
  { name: "Garissa", path: null },
  { name: "Homa Bay", path: null },
  { name: "Isiolo", path: null },
  { name: "Kajiado", path: null },
  { name: "Kakamega", path: null },
  { name: "Kericho", path: null },
  { name: "Kiambu", path: null },
  { name: "Kilifi", path: null },
  { name: "Kirinyaga", path: null },
  { name: "Kisii", path: null },
  { name: "Kisumu", path: null },
  { name: "Kitui", path: null },
  { name: "Kwale", path: null },
  { name: "Laikipia", path: null },
  { name: "Lamu", path: null },
  { name: "Machakos", path: null },
  { name: "Makueni", path: null },
  { name: "Mandera", path: null },
  { name: "Marsabit", path: null },
  { name: "Meru", path: null },
  { name: "Migori", path: null },
  { name: "Mombasa", path: null },
  { name: "Muranga", path: null },
  { name: "Nairobi", path: null },
  { name: "Nakuru", path: null },
  { name: "Nandi", path: null },
  { name: "Narok", path: null },
  { name: "Nyamira", path: null },
  { name: "Nyandarua", path: null },
  { name: "Nyeri", path: null },
  { name: "Samburu", path: null },
  { name: "Siaya", path: null },
  { name: "Taita Taveta", path: null },
  { name: "Tana River", path: null },
  { name: "Tharaka Nithi", path: null },
  { name: "Trans Nzoia", path: null },
  { name: "Turkana", path: null },
  { name: "Uasin Gishu", path: null },
  { name: "Vihiga", path: null },
  { name: "Wajir", path: null },
  { name: "West Pokot", path: null },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Navbar() {
  const [kpiOpen, setKpiOpen] = useState(false);
  const [countiesOpen, setCountiesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setKpiOpen(false);
    setCountiesOpen(false);
  }, [location]);

  const filtered = COUNTIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="bg-indigo-600 p-2 rounded-lg mr-3 group-hover:bg-indigo-700 transition">
              <FaDatabase className="text-white text-xl" />
            </div>
            <div>
              <span className="text-xl font-bold text-gray-900">StatData</span>
              <span className="text-xs text-indigo-600 block leading-none">Kenya Statistics</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/">Home</NavLink>

            {/* KPI Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setKpiOpen(!kpiOpen); setCountiesOpen(false); }}
                onBlur={() => setTimeout(() => setKpiOpen(false), 150)}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition ${kpiOpen ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                KPI
                <FaChevronDown className={`ml-1 text-xs transition-transform duration-200 ${kpiOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {kpiOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                  >
                    <div className="p-1">
                      <DropdownLink to="/population" color="indigo" onClick={() => setKpiOpen(false)}>
                        🧑‍🤝‍🧑 Population
                      </DropdownLink>
                      <DropdownLink to="/economy" color="green" onClick={() => setKpiOpen(false)}>
                        📈 Economy
                      </DropdownLink>
                      <DropdownLink to="/health" color="red" onClick={() => setKpiOpen(false)}>
                        ❤️ Health
                      </DropdownLink>
                      <DropdownLink to="/education" color="purple" onClick={() => setKpiOpen(false)}>
                        🎓 Education
                      </DropdownLink>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Counties Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setCountiesOpen(!countiesOpen); setKpiOpen(false); }}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition ${countiesOpen ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Counties
                <FaChevronDown className={`ml-1 text-xs transition-transform duration-200 ${countiesOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {countiesOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => { setCountiesOpen(false); setSearch(''); }} />
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                    >
                      <div className="p-2 border-b border-gray-100">
                        <input
                          type="text"
                          placeholder="🔍 Search county..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {filtered.map((county, index) => (
                          county.path ? (
                            <Link
                              key={index}
                              to={county.path}
                              onClick={() => { setCountiesOpen(false); setSearch(''); }}
                              className="flex items-center justify-between px-4 py-2 text-sm text-indigo-600 font-medium hover:bg-indigo-50"
                            >
                              {county.name}
                              <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Live</span>
                            </Link>
                          ) : (
                            <div key={index} className="flex items-center justify-between px-4 py-2 text-sm text-gray-400">
                              {county.name}
                              <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Soon</span>
                            </div>
                          )
                        ))}
                      </div>
                      <div className="p-2 border-t border-gray-100 text-xs text-gray-400 text-center bg-gray-50">
                        {filtered.length} of 47 counties
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <NavLink to="/compare">Compare</NavLink>
            <NavLink to="/about">About</NavLink>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
          >
            {mobileOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-3 space-y-1">
              <MobileNavLink to="/">🏠 Home</MobileNavLink>
              <div className="pt-2 pb-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-1">KPI</p>
                <MobileNavLink to="/population">🧑‍🤝‍🧑 Population</MobileNavLink>
                <MobileNavLink to="/economy">📈 Economy</MobileNavLink>
                <MobileNavLink to="/health">❤️ Health</MobileNavLink>
                <MobileNavLink to="/education">🎓 Education</MobileNavLink>
              </div>
              <div className="pt-2 pb-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-1">Counties</p>
                <MobileNavLink to="/counties/bomet">📍 Bomet</MobileNavLink>
              </div>
              <MobileNavLink to="/compare">⚖️ Compare</MobileNavLink>
              <MobileNavLink to="/about">ℹ️ About</MobileNavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function NavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-100'}`}
    >
      {children}
    </Link>
  );
}

function DropdownLink({ to, children, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition"
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      className={`block px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-100'}`}
    >
      {children}
    </Link>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50">
        <Navbar />

        {/* Page Content - padding top for fixed navbar */}
        <div className="pt-16">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/population" element={<Population />} />
            <Route path="/economy" element={<Economy />} />
            <Route path="/health" element={<Health />} />
            <Route path="/education" element={<Education />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/counties/bomet" element={<Bomet />} />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-600 p-2 rounded-lg mr-3">
                    <FaDatabase className="text-white text-xl" />
                  </div>
                  <span className="text-xl font-bold">StatData</span>
                </div>
                <p className="text-gray-400 text-sm">
                  Making Kenya's national statistics easy to understand, explore, and compare.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-gray-300">KPI Dashboards</h4>
                <div className="space-y-2">
                  <Link to="/population" className="block text-gray-400 hover:text-white text-sm transition">Population</Link>
                  <Link to="/economy" className="block text-gray-400 hover:text-white text-sm transition">Economy</Link>
                  <Link to="/health" className="block text-gray-400 hover:text-white text-sm transition">Health</Link>
                  <Link to="/education" className="block text-gray-400 hover:text-white text-sm transition">Education</Link>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-gray-300">Data Sources</h4>
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm">Kenya National Bureau of Statistics</p>
                  <p className="text-gray-400 text-sm">World Bank Open Data</p>
                  <p className="text-gray-400 text-sm">Central Bank of Kenya</p>
                  <p className="text-gray-400 text-sm">WHO & UNESCO</p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 text-center">
              <p className="text-gray-400 text-sm">
                © 2026 StatData. Data from KNBS, World Bank, and official sources.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;